import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import api, { setToken, removeToken } from '../api/client'

// Minimal types matching backend session response (no Supabase)
export interface AuthUser {
  id: string
  email: string
  is_platform_admin?: boolean
}

/** The caller's role inside the current organisation, not a global one. */
export type MemberRole = 'user' | 'volunteer' | 'coordinator' | 'admin' | 'owner'

export interface Profile {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: MemberRole
  avatar_url: string | null
  phone_number: string | null
  skills: string[] | null
  profile_completed: boolean
  created_at: string
  updated_at: string
}

interface AuthContextType {
  user: AuthUser | null
  profile: Profile | null
  volunteerMetadata: any | null
  volunteerRequest: any | null
  session: { user: AuthUser } | null
  loading: boolean
  profileLoading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  isVolunteer: boolean
  isAdmin: boolean
  isPlatformAdmin: boolean
  isPendingApproval: boolean
  needsVolunteerApplication: boolean
  needsProfileCompletion: boolean
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

const SESSION_CACHE_KEY = 'winterevent_session_cache'
const CACHE_EXPIRY = 5 * 60 * 1000 // 5 minutes

interface SessionCache {
  profile: Profile | null
  volunteerMetadata: any | null
  volunteerRequest: any | null
  timestamp: number
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [volunteerMetadata, setVolunteerMetadata] = useState<any>(null)
  const [volunteerRequest, setVolunteerRequest] = useState<any>(null)
  const [session, setSession] = useState<{ user: AuthUser } | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(true)
  const [isInitializing, setIsInitializing] = useState(false)
  const [queryFailureCount, setQueryFailureCount] = useState(0)
  const MAX_FAILURES = 3

  const getCachedData = useCallback((): SessionCache | null => {
    try {
      const cached = localStorage.getItem(SESSION_CACHE_KEY)
      if (!cached) return null
      const data: SessionCache = JSON.parse(cached)
      if (Date.now() - data.timestamp > CACHE_EXPIRY) {
        localStorage.removeItem(SESSION_CACHE_KEY)
        return null
      }
      return data
    } catch {
      localStorage.removeItem(SESSION_CACHE_KEY)
      return null
    }
  }, [])

  const setCachedData = useCallback((data: Omit<SessionCache, 'timestamp'>) => {
    try {
      localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({ ...data, timestamp: Date.now() }))
    } catch (e) {
      console.error('Error writing session cache:', e)
    }
  }, [])

  const clearCache = useCallback(() => {
    localStorage.removeItem(SESSION_CACHE_KEY)
  }, [])

  const fetchProfile = useCallback(async (userId: string, useCache = true) => {
    if (queryFailureCount >= MAX_FAILURES) {
      const cached = getCachedData()
      if (cached) {
        setProfile(cached.profile)
        setVolunteerMetadata(cached.volunteerMetadata)
        setVolunteerRequest(cached.volunteerRequest)
        setProfileLoading(false)
        setLoading(false)
        return
      }
    }
    if (useCache) {
      const cached = getCachedData()
      if (cached && cached.profile?.id === userId) {
        setProfile(cached.profile)
        setVolunteerMetadata(cached.volunteerMetadata)
        setVolunteerRequest(cached.volunteerRequest)
        setProfileLoading(false)
        setLoading(false)
        return
      }
    }
    setProfileLoading(true)
    try {
      const data = await api.auth.getSession()
      setQueryFailureCount(0)
      setProfile(data.profile || null)
      setVolunteerMetadata(data.volunteerMetadata || null)
      setVolunteerRequest(data.volunteerRequest || null)
      if (data.profile) {
        setCachedData({
          profile: data.profile,
          volunteerMetadata: data.volunteerMetadata || null,
          volunteerRequest: data.volunteerRequest || null,
        })
      }
    } catch (error) {
      console.error('Profile fetch error:', error)
      setQueryFailureCount((c) => c + 1)
      const cached = getCachedData()
      if (cached) {
        setProfile(cached.profile)
        setVolunteerMetadata(cached.volunteerMetadata)
        setVolunteerRequest(cached.volunteerRequest)
      } else {
        setProfile(null)
        setVolunteerMetadata(null)
        setVolunteerRequest(null)
      }
    } finally {
      setProfileLoading(false)
      setLoading(false)
    }
  }, [getCachedData, setCachedData, queryFailureCount])

  useEffect(() => {
    if (isInitializing) return
    setIsInitializing(true)
    const init = async () => {
      try {
        const token = localStorage.getItem('winterevent_token')
        if (!token) {
          setUser(null)
          setSession(null)
          setProfile(null)
          setVolunteerMetadata(null)
          setVolunteerRequest(null)
          setLoading(false)
          setProfileLoading(false)
          setIsInitializing(false)
          return
        }
        const data = await api.auth.getSession()
        const authUser = data.user as AuthUser
        setUser(authUser)
        setSession({ user: authUser })
        setProfile(data.profile || null)
        setVolunteerMetadata(data.volunteerMetadata || null)
        setVolunteerRequest(data.volunteerRequest || null)
        if (data.profile) {
          setCachedData({
            profile: data.profile,
            volunteerMetadata: data.volunteerMetadata || null,
            volunteerRequest: data.volunteerRequest || null,
          })
        }
      } catch {
        removeToken()
        setUser(null)
        setSession(null)
        setProfile(null)
        setVolunteerMetadata(null)
        setVolunteerRequest(null)
        clearCache()
      } finally {
        setLoading(false)
        setProfileLoading(false)
        setIsInitializing(false)
      }
    }
    init()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return
    const interval = setInterval(async () => {
      try {
        await api.auth.getSession()
      } catch {
        clearCache()
        removeToken()
        setUser(null)
        setSession(null)
        setProfile(null)
        setVolunteerMetadata(null)
        setVolunteerRequest(null)
      }
    }, 10 * 60 * 1000)
    return () => clearInterval(interval)
  }, [user, clearCache])

  const signIn = async (email: string, password: string) => {
    const res = await api.auth.login(email, password)
    if (res.token) setToken(res.token)
    setUser(res.user as AuthUser)
    setSession({ user: res.user as AuthUser })
    await fetchProfile(res.user.id, false)
  }

  const signOut = async () => {
    clearCache()
    removeToken()
    setUser(null)
    setSession(null)
    setProfile(null)
    setVolunteerMetadata(null)
    setVolunteerRequest(null)
    setQueryFailureCount(0)
  }

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user.id, false)
  }, [user, fetchProfile])

  const isPlatformAdmin = !!user?.is_platform_admin

  // Roles are hierarchical, so every check is "at least this role". Staff hold no
  // membership, so without the platform-admin flag they would render as a volunteer
  // inside a customer they are entitled to administer.
  const isVolunteer = useMemo(
    () => isPlatformAdmin || (!!profile && profile.role !== 'user'),
    [isPlatformAdmin, profile]
  )
  const isAdmin = useMemo(
    () => isPlatformAdmin || profile?.role === 'admin' || profile?.role === 'owner',
    [isPlatformAdmin, profile?.role]
  )
  const isPendingApproval = useMemo(() => volunteerRequest?.status === 'pending', [volunteerRequest?.status])
  const needsVolunteerApplication = useMemo(
    () =>
      !isPlatformAdmin &&
      !!(user && profile && !volunteerRequest && profile.role === 'user' && profile.profile_completed),
    [isPlatformAdmin, user, profile, volunteerRequest]
  )
  const needsProfileCompletion = useMemo(
    () => !isPlatformAdmin && !!(user && profile && !profile.profile_completed),
    [isPlatformAdmin, user, profile],
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        volunteerMetadata,
        volunteerRequest,
        session,
        loading,
        profileLoading,
        signIn,
        signOut,
        refreshProfile,
        isVolunteer,
        isAdmin,
        isPlatformAdmin,
        isPendingApproval,
        needsVolunteerApplication,
        needsProfileCompletion,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
